'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Button from '../ui/Button';

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const servicesRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileButtonRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close services dropdown if clicked outside
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setServicesOpen(false);
      }

      // Close mobile menu if clicked outside
      if (menuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target) &&
        mobileButtonRef.current &&
        !mobileButtonRef.current.contains(event.target)) {
        setMenuOpen(false);
        setMobileServicesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [menuOpen]);

  // Updated main navigation order per request
  const menuItems = [
    { label: 'About Us', link: '/about' },
    { label: 'Services', link: '/services' },
    { label: 'Infrastructure', link: '/infrastructure' },
    { label: 'Our Work', link: '/portfolio' },
    { label: 'Blog', link: '/blog' },
    { label: 'Certifications', link: '/certifications' },
    { label: 'Careers', link: '/careers' },
    { label: 'Contact', link: '/contact' }
  ];

  const servicesSubmenu = [
    { label: 'Sheet Form Labels', link: '/services/sheet-form-labels' },
    { label: 'Roll Form Labels', link: '/services/roll-form-labels' },
    { label: 'Security Labels', link: '/services/security-labels' },
    { label: 'Digital Packaging Labels', link: '/services/digital-packaging-labels' }
  ];

  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`w-full fixed top-0 z-[100] transition-all duration-300 ${scrolled
        ? 'bg-white shadow-md border-b border-gray-100'
        : 'bg-white/90 backdrop-blur-xl border-b border-white/20'
        }`}
    >
      <div className="rpl-header-inner">
        {/* Logo */}
        <Link href="/" aria-label="Royal Prime Labels home" className="no-tap-enforce flex items-center transform hover:scale-105 transition-transform duration-300">
          <Image
            src="/images/horizontal-logo.webp"
            alt="Royal Prime Labels"
            width={260}
            height={70}
            priority
            sizes="(max-width: 480px) 160px, (max-width: 768px) 200px, (max-width: 1280px) 220px, 260px"
            className="rpl-logo transition-transform duration-300"
          />
        </Link>

        {/* Hamburger Menu — visible below xl: (1280px) */}
        <button
          ref={mobileButtonRef}
          className="block xl:hidden p-2 text-[#0a1f33] min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label="Open menu"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 8h16M4 16h16" />
          </svg>
        </button>

        {/* Desktop Navigation — visible from xl: (1280px) upwards */}
        <nav className="rpl-nav-desktop">
          {menuItems?.map((item, index) => {
            if (item.label === 'Services') {
              return (
                <div
                  key={index}
                  className="relative group flex items-center h-full"
                  ref={servicesRef}
                  onMouseEnter={() => {
                    if (timeoutRef.current) clearTimeout(timeoutRef.current);
                    setServicesOpen(true);
                  }}
                  onMouseLeave={() => {
                    timeoutRef.current = setTimeout(() => {
                      setServicesOpen(false);
                    }, 150);
                  }}
                >
                  <Link
                    href="/services"
                    role="menuitem"
                    aria-haspopup="true"
                    aria-expanded={servicesOpen}
                    className="rpl-nav-link no-tap-enforce px-[2px]"
                  >
                    Services
                    <svg className={`w-3 h-3 transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </Link>
                  {servicesOpen && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50">
                      <ul
                        role="menu"
                        className="w-64 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-slate-100 p-2 overflow-hidden"
                      >
                        {servicesSubmenu?.map((s, si) => (
                          <li key={si} role="menuitem">
                            <Link
                              href={s.link}
                              className="block w-full text-left px-5 py-3.5 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-blue-600 rounded-xl transition-all duration-200"
                              onClick={() => setServicesOpen(false)}
                            >
                              {s?.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link
                key={index}
                href={item.link}
                className="rpl-nav-link no-tap-enforce"
              >
                {item?.label}
              </Link>
            );
          })}
        </nav>

        {/* Desktop Contact & CTA — visible from xl: (1280px) upwards */}
        <div className="rpl-nav-cta">
          <Link href="/quote">
            <Button
              variant="primary"
              className="rounded-full px-[16px] py-[10px] xl:px-[28px] xl:py-[14px] text-[12px] xl:text-[14px] font-bold tracking-widest uppercase bg-[#0a1f33] text-white hover:bg-blue-600 shadow-[0_10px_20px_rgba(10,31,51,0.2)] hover:shadow-[0_15px_30px_rgba(37,99,235,0.3)] transition-all duration-300"
              rightImage={{
                src: "/images/img_arrowright.svg",
                width: 14,
                height: 12
              }}
            >
              Get a Quote
            </Button>
          </Link>
        </div>

        {/* Mobile Menu */}
          <nav
            ref={mobileMenuRef}
            className={`${menuOpen ? 'block' : 'hidden'} xl:hidden absolute top-full left-0 w-full bg-white shadow-lg border-t border-gray-200 z-50 overflow-y-auto max-h-[80vh]`}
          >
            <div className="px-4 py-6 space-y-4">
              {menuItems?.filter(item => item.label !== 'Contact').map((item, index) => {
                if (item.label === 'Services') {
                  return (
                    <div key={index} className="w-full">
                      <button
                        onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                        className="flex items-center justify-between w-full text-left py-2 type-body font-semibold text-global-5 hover:text-global-9 transition-colors duration-200"
                      >
                        {item.label}
                        <img
                          src="/images/img_arrow_down.svg"
                          alt=""
                          className={`w-[20px] h-[20px] transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`}
                        />
                      </button>

                      {/* Mobile Services Submenu */}
                      <div
                        className={`pl-4 space-y-3 overflow-hidden transition-all duration-300 ${mobileServicesOpen ? 'max-h-[500px] mt-2 pb-2' : 'max-h-0'}`}
                      >
                        <Link
                          href="/services"
                          className="block w-full text-left py-2 text-sm font-medium text-global-5 hover:text-global-9"
                          onClick={() => {
                            setMenuOpen(false);
                            setMobileServicesOpen(false);
                          }}
                        >
                          All Services
                        </Link>
                        {servicesSubmenu?.map((subItem, subIndex) => (
                          <Link
                            key={subIndex}
                            href={subItem.link}
                            className="block w-full text-left py-2 text-sm font-medium text-gray-600 hover:text-global-9"
                            onClick={() => {
                              setMenuOpen(false);
                              setMobileServicesOpen(false);
                            }}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }

                return (
                  <Link
                    key={index}
                    href={item.link}
                    className="block w-full text-left py-2 type-body font-semibold text-global-5 hover:text-global-9 transition-colors duration-200"
                    onClick={() => setMenuOpen(false)}
                  >
                    {item?.label}
                  </Link>
                );
              })}
              <div className="border-t border-gray-200 pt-4 space-y-3">
                <Link href="/contact" className="block w-full text-left py-2 type-body font-semibold text-global-3" onClick={() => setMenuOpen(false)}>
                  Contact
                </Link>
                <Link href="/quote" onClick={() => setMenuOpen(false)}>
                  <Button
                    variant="primary"
                    fullWidth
                    className="rounded-[24px] py-[12px] text-[15px] font-semibold"
                    rightImage={{
                      src: "/images/img_arrowright.svg",
                      width: 14,
                      height: 12
                    }}
                  >
                    Schedule Consultation
                  </Button>
                </Link>
              </div>
            </div>
          </nav>
      </div>
    </header>
  );
};


export default Header;